import type { BaseModelAttributes } from './base';
import { I18nLabels } from './common';
import { Locale } from './locale';
import type { SubtitleStyleSpan } from '../utils/subtitleInlineStyles';
import type { ClipEffectItem } from '../utils/clipEffects';

export enum VideoClipStatus {
  DRAFT = 'draft',
  STORYBOARD_GENERATION = 'storyboard_generation',
  IMAGE_GENERATION = 'image_generation',
  VOICE_GENERATION = 'voice_generation',
  // SOUND_GENERATION = 'sound_generation',
  VIDEO_GENERATION = 'video_generation',
  LIP_SYNC = 'lip_sync',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export const VideoClipStatusLabels: I18nLabels<VideoClipStatus> = {
  [VideoClipStatus.DRAFT]: { [Locale.ZH_CN]: '草稿', [Locale.EN_US]: 'Draft' },
  [VideoClipStatus.STORYBOARD_GENERATION]: { [Locale.ZH_CN]: '分镜生成', [Locale.EN_US]: 'Storyboard Gen' },
  [VideoClipStatus.IMAGE_GENERATION]: { [Locale.ZH_CN]: '图片生成', [Locale.EN_US]: 'Image Gen' },
  [VideoClipStatus.VOICE_GENERATION]: { [Locale.ZH_CN]: '配音生成', [Locale.EN_US]: 'Voice Gen' },
  // [VideoClipStatus.SOUND_GENERATION]: { [Locale.ZH_CN]: '音效生成', [Locale.EN_US]: 'Sound Gen' },
  [VideoClipStatus.VIDEO_GENERATION]: { [Locale.ZH_CN]: '视频生成', [Locale.EN_US]: 'Video Gen' },
  [VideoClipStatus.LIP_SYNC]: { [Locale.ZH_CN]: '视频对口型', [Locale.EN_US]: 'Lip Sync' },
  [VideoClipStatus.COMPLETED]: { [Locale.ZH_CN]: '已完成', [Locale.EN_US]: 'Completed' },
  [VideoClipStatus.FAILED]: { [Locale.ZH_CN]: '失败', [Locale.EN_US]: 'Failed' },
};

export interface IVideoClip extends BaseModelAttributes {
  title: string;
  description: string;
  sequence: number;
  script: string;
  speaker?: string;
  sceneName?: string;
  startImage?: string;  // 开始帧图片URL
  endImage?: string;    // 结束帧图片URL
  storyboardImage?: string;  // 分镜图片URL
  videoUrl?: string;
  scriptAudioUrl?: string;
  soundEffect?: string;
  soundEffectAudioUrl?: string;
  duration?: number;
  structure?: string; // 
  cameraWork?: string; //
  transition?: string;
  emotion?: string;
  subtitleStyleSpans?: SubtitleStyleSpan[] | string;
  effectItems?: ClipEffectItem[] | string;
  status: VideoClipStatus;
  projectId: string;
  episodeId: string;
  userId: string;
  // 最后一次生成参数记录
  lastStartImageParams?: string;  // JSON字符串存储开始图片生成参数
  lastEndImageParams?: string;    // JSON字符串存储结束图片生成参数
  lastStoryboardImageParams?: string;  // JSON字符串存储分镜图片生成参数
  lastAudioParams?: string;       // JSON字符串存储音频生成参数
  lastVideoParams?: string;       // JSON字符串存储视频生成参数
  lastSoundEffectParams?: string; // JSON字符串存储音效生成参数
  lastLipSyncParams?: string;     // JSON字符串存储对口型参数
  episode?: {
    id: string;
    name: string;
  };
}

export interface CreateVideoClipRequest {
  title: string;
  description: string;
  sequence: number;
  script: string;
  speaker?: string;
  sceneName?: string;
  startImage?: string;
  endImage?: string;
  storyboardImage?: string;
  videoUrl?: string;
  scriptAudioUrl?: string;
  soundEffect?: string;
  soundEffectAudioUrl?: string;
  duration?: number;
  structure?: string;
  cameraWork?: string;
  transition?: string;
  emotion?: string;
  subtitleStyleSpans?: SubtitleStyleSpan[] | string;
  effectItems?: ClipEffectItem[] | string;
  status?: VideoClipStatus;
  projectId: string;
  episodeId: string;
  userId?: string;
  lastStartImageParams?: string;
  lastEndImageParams?: string;
  lastStoryboardImageParams?: string;
  lastAudioParams?: string;
  lastVideoParams?: string;
  lastSoundEffectParams?: string;
}

export interface UpdateVideoClipRequest {
  title?: string;
  description?: string;
  sequence?: number;
  script?: string;
  speaker?: string;
  sceneName?: string;
  startImage?: string;
  endImage?: string;
  storyboardImage?: string;
  videoUrl?: string;
  scriptAudioUrl?: string;
  soundEffect?: string;
  soundEffectAudioUrl?: string;
  duration?: number;
  structure?: string;
  cameraWork?: string;
  transition?: string;
  emotion?: string;
  subtitleStyleSpans?: SubtitleStyleSpan[] | string;
  effectItems?: ClipEffectItem[] | string;
  status?: VideoClipStatus;
  lastStartImageParams?: string;
  lastEndImageParams?: string;
  lastStoryboardImageParams?: string;
  lastAudioParams?: string;
  lastVideoParams?: string;
  lastSoundEffectParams?: string;
  lastLipSyncParams?: string;
}

export interface VideoClipQuery {
  search?: string;
  projectId?: string;
  episodeId?: string;
  status?: VideoClipStatus;
  userId?: string;
}
